<template>
    <BaseNotification theme="info" hide-dismiss>
        <template #content>
            <div class="flex flex-col gap-3">
                <p class="body-small-bold">{{ reportEligibilityCopy(progress) }}</p>
                <div class="flex flex-col gap-1">
                    <p class="body-small">{{ t("components.assessment.thresholdsIntro") }}</p>
                    <ul class="flex flex-col gap-1" :class="showCounts ? '' : 'list-disc pl-5'">
                        <li v-for="category in progress.categories" :key="category.value" class="body-small" :class="showCounts ? 'flex items-start gap-2' : ''">
                            <template v-if="!showCounts">{{ thresholdRequirementLabel(category) }}</template>
                            <template v-else>
                                <i class="bi mt-1" :class="isMet(category) ? 'bi-check-circle-fill text-ink' : 'bi-circle text-ink-muted'" aria-hidden="true"></i>
                                <span :class="isMet(category) ? 'line-through text-ink-muted' : ''">
                                    {{ thresholdRequirementLabel(category) }}
                                    <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: dash before the count -->
                                    <span class="text-ink-muted">&mdash; {{ countLabel(category) }}</span>
                                </span>
                                <span class="sr-only">{{ statusLabel(category) }}</span>
                            </template>
                        </li>
                    </ul>
                </div>
            </div>
        </template>
    </BaseNotification>
</template>

<script setup lang="ts">
import type { CategoryProgress, CategoryThresholdProgress } from "~vue/api/generated/reflections";
import BaseNotification from "~vue/components/BaseNotification.vue";
import { nominationMet, reportEligibilityCopy, thresholdRequirementLabel } from "~vue/utils/categoryThresholds";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
    progress: CategoryThresholdProgress;
    // Per-category invite counts are only real where the subject categorizes
    // raters; elsewhere an invite carries no category until the rater answers.
    showInvited: boolean;
    // Responses only exist once the cycle is collecting, and they are what
    // unlocks the report, so they also decide the tick when shown.
    showSubmitted: boolean;
}>();

const showCounts = computed(() => props.showInvited || props.showSubmitted);

function isMet(category: CategoryProgress): boolean {
    return props.showSubmitted ? category.met : nominationMet(category);
}

function countLabel(category: CategoryProgress): string {
    const counts: string[] = [];
    if (props.showInvited) {
        counts.push(t("components.assessment.invitedCount", { count: category.nominated }));
    }
    if (props.showSubmitted) {
        counts.push(t("components.assessment.submittedCount", { count: category.submitted }));
    }
    return counts.join(", ");
}

function statusLabel(category: CategoryProgress): string {
    if (props.showSubmitted) {
        return isMet(category) ? t("components.assessment.thresholdMet") : t("components.assessment.thresholdNotMet");
    }
    return isMet(category) ? t("components.assessment.enoughRaters") : t("components.assessment.needsMoreRaters");
}
</script>
